import {
	format,
	addDays,
	addWeeks,
	addMonths,
	subDays,
	subWeeks,
	subMonths,
	startOfWeek,
	endOfWeek,
} from 'date-fns';
import { Button } from '../Button';
import {
	CalendarIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	MagnifyingGlassPlusIcon,
	MagnifyingGlassMinusIcon,
} from '@heroicons/react/24/outline';
import { classNames } from '../helpers/functions';

export function DateNavigation({
	selectedDate,
	onDateChange,
	viewMode,
	onViewModeChange,
	// Zoom controls (optional, only used in day view)
	onZoomIn,
	onZoomOut,
	canZoomIn,
	canZoomOut,
	children,
}) {
	const goToPrevious = () => {
		switch (viewMode) {
			case 'day':
				onDateChange(subDays(selectedDate, 1));
				break;
			case 'week':
				onDateChange(subWeeks(selectedDate, 1));
				break;
			case 'month':
				onDateChange(subMonths(selectedDate, 1));
				break;
		}
	};

	const goToNext = () => {
		switch (viewMode) {
			case 'day':
				onDateChange(addDays(selectedDate, 1));
				break;
			case 'week':
				onDateChange(addWeeks(selectedDate, 1));
				break;
			case 'month':
				onDateChange(addMonths(selectedDate, 1));
				break;
		}
	};

	const goToToday = () => {
		onDateChange(new Date());
	};

	const getDateRangeLabel = () => {
		switch (viewMode) {
			case 'day':
				return format(selectedDate, 'EEEE, MMMM d, yyyy');
			case 'week': {
				const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
				const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
				return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
			}
			case 'month':
				return format(selectedDate, 'MMMM yyyy');
		}
	};

	return (
		<div className="flex justify-between flex-wrap items-center gap-2 p-3 bg-gray-50 border-b">
			<div>
				{/* Additional children (e.g., Add Asset button) */}
				{children}
			</div>

			<div className="flex flex-wrap gap-2">
				{/* Zoom Controls - only show in day view when handlers are provided */}
				{viewMode === 'day' && onZoomIn && onZoomOut && (
					<div className="flex items-center gap-1 ml-auto">
						<span className="text-xs text-gray-500 mr-1">Zoom:</span>
						<button
							onClick={onZoomOut}
							disabled={!canZoomOut}
							className={classNames(
								'p-1.5 rounded-md border transition-colors',
								canZoomOut
									? 'hover:bg-gray-100 border-gray-300 text-gray-600 bg-white'
									: 'border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50',
							)}
							title="Zoom out"
						>
							<MagnifyingGlassMinusIcon className="w-4 h-4" />
						</button>
						<button
							onClick={onZoomIn}
							disabled={!canZoomIn}
							className={classNames(
								'p-1.5 rounded-md border transition-colors',
								canZoomIn
									? 'hover:bg-gray-100 border-gray-300 text-gray-600 bg-white'
									: 'border-gray-200 text-gray-300 cursor-not-allowed bg-gray-50',
							)}
							title="Zoom in"
						>
							<MagnifyingGlassPlusIcon className="w-4 h-4" />
						</button>
					</div>
				)}

				<div className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-gray-300 bg-white">
					<CalendarIcon className="h-4 w-4 text-gray-500" />
					<span className="text-sm font-medium text-gray-900">
						{getDateRangeLabel()}
					</span>
				</div>

				<div className="flex items-center gap-1">
					<button
						onClick={goToPrevious}
						className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
					>
						<ChevronLeftIcon className="h-4 w-4" />
					</button>
					<button
						onClick={goToNext}
						className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
					>
						<ChevronRightIcon className="h-4 w-4" />
					</button>
				</div>

				<Button
					type="secondary"
					onClick={goToToday}
					className="!ml-0 !py-1.5 mr-2"
				>
					Today
				</Button>

				{/* View mode toggle */}
				<div className="flex rounded-lg border bg-white p-1">
					{['day', 'week', 'month'].map((mode) => (
						<button
							key={mode}
							onClick={() => onViewModeChange(mode)}
							className={classNames(
								'px-3 py-1 text-xs font-medium rounded-md capitalize transition-colors',
								viewMode === mode
									? 'bg-indigo-600 text-white'
									: 'text-gray-700 hover:bg-gray-100',
							)}
						>
							{mode}
						</button>
					))}
				</div>
			</div>
		</div>
	);
}
